import React, { useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, PhoneCall, ExternalLink, FileText, History, Bot } from 'lucide-react';
import { dispositionMeta } from '@/lib/dispositions';

const QUICK_REMARKS = ['Call not picked', 'Asked to call later', 'Interested — will visit', 'Price concern', 'Wants home visit'];

const KIND_META = {
  note: { icon: MessageSquare, label: 'Remark', tone: 'bg-stone-900 text-white' },
  call: { icon: PhoneCall, label: 'Call', tone: 'bg-rose-600 text-white' },
  file: { icon: FileText, label: 'File', tone: 'bg-sky-600 text-white' },
  fundle: { icon: ExternalLink, label: 'Fundle', tone: 'bg-amber-500 text-white' },
  system: { icon: History, label: 'Update', tone: 'bg-stone-300 text-stone-800' },
};

const initials = (n) => (n || 'S').split(/\s+/).map((x) => x[0]).slice(0, 2).join('').toUpperCase();

function describe(ev) {
  if (ev.kind === 'disposition_change') return <>Status: <span className="text-stone-500">{dispositionMeta(ev.from_value).label}</span> → <b>{dispositionMeta(ev.to_value).label}</b></>;
  if (ev.kind === 'assignment') return <>Assigned to <b>{ev.to_value || 'nobody'}</b></>;
  if (ev.kind === 'created') return <>Patient created</>;
  if (ev.kind === 'system') return <><b>{ev.body}</b>: <span className="text-stone-500">{ev.from_value || '(empty)'}</span> → <b>{ev.to_value || '(empty)'}</b></>;
  return ev.body;
}

export const RemarksLog = ({ ticketId, activities, fundleComment, onAdded }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [onlyRemarks, setOnlyRemarks] = useState(false);

  const entries = useMemo(() => {
    const list = activities.map((a) => ({ ...a, _kind: ['note', 'call', 'file'].includes(a.kind) ? a.kind : 'system' }));
    if (fundleComment) list.push({ id: 'fundle', _kind: 'fundle', body: fundleComment, actor_name: 'Fundle SupportHub', timestamp: null });
    return onlyRemarks ? list.filter((e) => e._kind === 'note' || e._kind === 'fundle' || e._kind === 'call') : list;
  }, [activities, fundleComment, onlyRemarks]);

  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/tickets/${ticketId}/note`, { body: note.trim() });
      setNote('');
      onAdded && onAdded();
    } catch { toast.error('Failed to add remark'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="remarks-log">
      <div className="rounded-md border border-stone-200 p-2 bg-stone-50">
        <Textarea
          value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder="Add a remark — it is logged with your name and time…"
          className="resize-none bg-white text-sm" data-testid="ticket-note-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save(); }}
        />
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          {QUICK_REMARKS.map((q) => (
            <button key={q} type="button" onClick={() => setNote(q)}
              className="text-[10px] px-2 py-0.5 rounded-full border border-stone-300 bg-white hover:bg-stone-900 hover:text-white transition-colors"
              data-testid={`quick-remark-${q.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{q}</button>
          ))}
          <Button size="sm" className="h-7 ml-auto bg-stone-900 hover:bg-stone-800 text-white" onClick={save} disabled={saving || !note.trim()} data-testid="ticket-note-submit">
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Log remark
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Remarks log · {entries.length}</span>
        <button type="button" onClick={() => setOnlyRemarks((v) => !v)} className="ml-auto text-[10px] text-stone-600 hover:text-stone-900 underline-offset-2 hover:underline" data-testid="remarks-filter-toggle">
          {onlyRemarks ? 'Show all activity' : 'Remarks & calls only'}
        </button>
      </div>

      {entries.length === 0 ? (
        <div className="text-xs text-stone-400 italic">No remarks yet.</div>
      ) : (
        <ol className="relative border-l border-stone-200 ml-3 space-y-3">
          {entries.map((ev) => <LogEntry key={ev.id} ev={ev} />)}
        </ol>
      )}
    </div>
  );
};

function LogEntry({ ev }) {
  const meta = KIND_META[ev._kind];
  const Icon = (ev.source === 'ai-call' && ev._kind === 'note') ? Bot : meta.icon;
  const big = ev._kind === 'note' || ev._kind === 'fundle' || ev._kind === 'call';
  return (
    <li className="pl-5 relative" data-testid={`remark-entry-${ev.id}`}>
      <span className={`absolute -left-[11px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center ring-2 ring-white ${meta.tone}`}>
        <Icon className="w-2.5 h-2.5" />
      </span>
      <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
        <span className="w-4 h-4 rounded-full bg-stone-200 text-stone-700 text-[8px] font-bold flex items-center justify-center">{initials(ev.actor_name)}</span>
        <span className="font-semibold text-stone-700">{ev.actor_name || 'System'}</span>
        <span className="uppercase tracking-wider">· {meta.label}</span>
        <span className="ml-auto tabular-nums">{ev.timestamp ? new Date(ev.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'migrated'}</span>
      </div>
      <div className={`mt-1 text-xs whitespace-pre-wrap leading-relaxed ${big ? 'p-2 rounded-md border bg-white border-stone-200 text-stone-800' : 'text-stone-600'} ${ev._kind === 'fundle' ? 'bg-amber-50 border-amber-200' : ''}`}>
        {describe(ev)}
      </div>
    </li>
  );
}
